import { Box } from '@chakra-ui/react'
import { Button, Spinner, Table, useToast } from '@missionlabs/react'
import { SpeedDial, UserRole } from '@missionlabs/types'
import { createColumnHelper } from '@tanstack/react-table'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFormatToNumberE164 } from 'shared/hooks/useFormatToNumberE164'
import { useUserRole } from 'shared/hooks/useUserRole'
import {
    useDeleteSpeedDialForClientMutation,
    useDeleteSpeedDialForUserMutation
} from 'shared/store'

export interface SpeedDialListProps {
    speedDials: SpeedDial[]
    isLoading: boolean
    allowEditing?: boolean
    maxPerPage?: number
}

export const SpeedDialTable: FC<SpeedDialListProps> = ({
    speedDials,
    isLoading,
    allowEditing = true,
    maxPerPage = 10
}) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const userRole = useUserRole()
    const { pathname } = useLocation()
    const { formatToLocalNumber } = useFormatToNumberE164()
    const navigate = useNavigate()
    const columnHelper = createColumnHelper<SpeedDial>()

    const isAdmin = pathname.includes('/admin/') && userRole === UserRole.ADMIN

    const deleteRequest = isAdmin
        ? useDeleteSpeedDialForClientMutation
        : useDeleteSpeedDialForUserMutation

    const [deleteSpeedDial, { isLoading: isDeleteLoading }] = deleteRequest()

    const onRemoveUpdate = useCallback(
        async (speedDial: SpeedDial) => {
            try {
                await deleteSpeedDial(speedDial).unwrap()
                toast({ status: 'success', title: `Deleted speed dial` })
            } catch (e) {
                console.log(e)
                toast({ status: 'error', title: 'Failed to delete speed dial' })
            }
        },
        [deleteSpeedDial, toast]
    )

    const renderRemoveButton = useCallback(
        (row: SpeedDial) => {
            return (
                <Button
                    variant="secondary"
                    size="sm"
                    fontWeight="500"
                    isLoading={isDeleteLoading}
                    onClick={() => onRemoveUpdate(row)}
                    isDisabled={!allowEditing}
                >
                    {t('Remove')}
                </Button>
            )
        },
        [isDeleteLoading, allowEditing, t, onRemoveUpdate]
    )

    const columns = useMemo(() => {
        const cols = [
            columnHelper.accessor('speedDialCode', {
                cell: info => info.getValue(),
                header: t('settings.speedDial.code'),
                size: 0
            }),
            columnHelper.accessor('name', {
                cell: info => info.getValue(),
                header: t('settings.speedDial.name')
            }),
            columnHelper.accessor('replacementNumber', {
                cell: info => formatToLocalNumber(info.getValue()),
                header: t('settings.speedDial.number')
            })
        ]

        if (allowEditing)
            cols.push(
                columnHelper.display({
                    cell: ({ row }) => renderRemoveButton(row.original),
                    id: 'remove-speed-dial',
                    size: 0,
                    meta: {
                        clickable: false
                    }
                })
            )

        return cols
    }, [allowEditing, columnHelper, formatToLocalNumber, renderRemoveButton, t])

    if (isLoading) {
        return <Spinner size="sm" />
    }

    return (
        <Box w="100%">
            <Table
                data={speedDials}
                columns={columns}
                paginate
                sorting={[{ id: 'speedDialCode', desc: false }]}
                onRowClicked={allowEditing ? row => navigate(row.speedDialCode) : undefined}
                pageSize={maxPerPage}
            />
        </Box>
    )
}
